import React, { useState } from "react";
import { Paper, Text, Badge, Group, Avatar, Tooltip } from "@mantine/core";
import {
  IconMessageCircle,
  IconPaperclip,
  IconCalendar,
  IconAlertCircle,
} from "@tabler/icons-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import QuickActionsMenu from "./QuickActionsMenu";

const priorityColors = {
  low: "#94929e",
  medium: "#f59e0b",
  high: "#ea580c",
  urgent: "#bd5555",
};

const WorkflowCard = ({ task, users, onClick, onUpdate, isDragging }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0 : 1, // Hide the original spot
    cursor: isDragging ? "grabbing" : "pointer",
    boxShadow: isDragging ? "var(--mantine-shadow-xl)" : undefined,
    transformOrigin: "center",
  };

  const assignee = users.find((u) => u.id === task.assignedToId);
  const commentCount = task.comments?.length || 0;
  const attachmentCount = task.attachments?.length || 0;

  // Парсимо tags якщо це JSON рядок
  let tags = task.tags || [];
  if (typeof tags === "string") {
    try {
      tags = JSON.parse(tags);
    } catch (e) {
      tags = [];
    }
  }
  if (!Array.isArray(tags)) {
    tags = [];
  }

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24));

    const formatted = d.toLocaleDateString("uk-UA", {
      day: "2-digit",
      month: "short",
    });

    // Determine urgency
    let urgency = "normal";
    let icon = null;
    if (diffDays < 0) {
      urgency = "overdue";
      icon = <IconAlertCircle size={12} color="red" />;
    } else if (diffDays <= 2) {
      urgency = "soon";
      icon = <IconAlertCircle size={12} color="#ea580c" />;
    }

    return { formatted, urgency, icon, diffDays };
  };

  const dueDateInfo = task.dueDate ? formatDate(task.dueDate) : null;

  return (
    <Paper
      ref={setNodeRef}
      className="premium-workflow-card"
      style={{
        ...style,
        borderColor: isHovered ? priorityColors[task.priority] : "#e7e7eb",
      }}
      {...attributes}
      {...listeners}
      shadow={isHovered ? "sm" : "xs"}
      p="sm"
      withBorder
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Priority Badge and Quick Actions */}
      <Group justify="space-between" mb="xs">
        <Badge
          size="sm"
          color={
            task.priority === "urgent"
              ? "red"
              : task.priority === "high"
                ? "orange"
                : task.priority === "medium"
                  ? "yellow"
                  : "gray"
          }
          variant="light"
        >
          {task.priority}
        </Badge>
        <div
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          {(isHovered || menuOpened) && (
            <QuickActionsMenu
              opened={menuOpened}
              task={task}
              users={users}
              onUpdate={onUpdate}
              onOpenChange={setMenuOpened}
            />
          )}
        </div>
      </Group>

      {/* Due Date with Urgency */}
      {dueDateInfo && (
        <Group gap={4} mb="xs">
          {dueDateInfo.icon}
          <IconCalendar size={12} />
          <Text
            size="xs"
            c={
              dueDateInfo.urgency === "overdue"
                ? "red"
                : dueDateInfo.urgency === "soon"
                  ? "orange"
                  : "dimmed"
            }
            fw={dueDateInfo.urgency !== "normal" ? 600 : 400}
          >
            {dueDateInfo.formatted}
            {dueDateInfo.urgency === "overdue" && " (Overdue!)"}
            {dueDateInfo.urgency === "soon" &&
              ` (${dueDateInfo.diffDays}d left)`}
          </Text>
        </Group>
      )}

      {/* Title */}
      <Text fw={500} size="sm" mb="xs" lineClamp={2}>
        {task.title}
      </Text>

      {/* Tags */}
      {tags && tags.length > 0 && (
        <Group gap={4} mb="xs">
          {tags.slice(0, 3).map((tag, index) => (
            <Badge key={index} size="xs" variant="light">
              {tag}
            </Badge>
          ))}
          {tags.length > 3 && (
            <Text size="xs" c="dimmed">
              +{tags.length - 3}
            </Text>
          )}
        </Group>
      )}

      {/* Footer */}
      <Group justify="space-between" mt="xs">
        <Group gap={8}>
          {commentCount > 0 && (
            <Group gap={4}>
              <IconMessageCircle size={14} />
              <Text size="xs" c="dimmed">
                {commentCount}
              </Text>
            </Group>
          )}
          {attachmentCount > 0 && (
            <Group gap={4}>
              <IconPaperclip size={14} />
              <Text size="xs" c="dimmed">
                {attachmentCount}
              </Text>
            </Group>
          )}
        </Group>

        <Group gap={8}>
          {task.creator && (
            <Tooltip label={`Created by: ${task.creator.email}`} withArrow>
              <Avatar size={20} radius="xl" variant="light" color="gray">
                {task.creator.email.charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
          )}

          {assignee && (
            <Tooltip label={`Assigned to: ${assignee.email}`} withArrow>
              <Avatar size={24} radius="xl" color="blue">
                {assignee.email.charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
          )}
        </Group>
      </Group>
    </Paper>
  );
};

export default WorkflowCard;
